import type { AccountDashboardResponse } from './apiClientResponses';
import type { Root } from './prototypeTypes';
import { renderEmptyStateMarkup } from './emptyStateMarkup';
import { formatRelative } from './formatUtils';
import { renderHistoryEntry } from './historyEntryRenderer';

type ProfileHistoryItem = NonNullable<AccountDashboardResponse['history']>[number];

/** Paints the profile's learning-history summary as an independent view fragment. */
export function paintProfileHistory(root: Root, history: ProfileHistoryItem[] | undefined, unavailable = false): void {
	const node = root.querySelector<HTMLElement>('[data-bind="profile.history"]');
	if (!node) return;
	// Profile can hydrate again after session reconciliation while the same
	// History preview is already visible. Keep its existing rows (and decoded
	// thumbnail images) when the server projection is unchanged instead of
	// tearing down and recreating all three entries.
	const renderKey = unavailable ? 'unavailable' : profileHistoryRenderKey(history);
	if (node.dataset.profileHistoryRenderKey === renderKey) return;
	node.dataset.profileHistoryRenderKey = renderKey;
	if (unavailable) {
		const failed = document.createElement('div');
		failed.innerHTML = renderEmptyStateMarkup({ icon: 'clock', title: 'History unavailable', subtitle: 'Please reopen Profile to try again.' });
		node.replaceChildren(failed);
		return;
	}
	if (!history || history.length === 0) {
		const empty = document.createElement('div');
		empty.innerHTML = renderEmptyStateMarkup({ icon: 'clock', title: 'No history yet', subtitle: 'Open a chapter to start tracking progress.' });
		node.replaceChildren(empty);
		return;
	}
	node.replaceChildren(...history.map(renderHistoryEntry));
}

function profileHistoryRenderKey(history: ProfileHistoryItem[] | undefined): string {
	if (!history || history.length === 0) return 'empty';
	return history.map((item) => [
		item.topicSlug,
		item.chapterId,
		item.chapterIndex,
		item.progressPercent,
		item.updatedAt,
		formatRelative(item.updatedAt),
		item.topicTitle,
		item.chapterTitle,
		item.coverImageUrl,
	].map((value) => String(value ?? '')).join('\u001f')).join('\u001e');
}
